import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  current,
} from '@reduxjs/toolkit';
import { getUserCountry } from './thunks';
import { Dir, Languages, localizationSystemValues } from './types';
import { AlertState, CommonState, ErrorState, LoadingState } from './types';
export const name = 'common';

export const getUserCountryAsync = createAsyncThunk(
  name + '/getUserCountryAsync',
  getUserCountry
);

const localStorageLanguage = (localStorage.getItem('i18nextLng') ||
  'ar') as keyof typeof Languages;

const initialState: CommonState = {
  alert: {
    show: false,
    type: null,
    messages: null,
    for: 'page',
  },
  error: {
    show: false,
    type: null,
  },
  loading: {
    show: false,
    ref: null,
    isScreenLoader: true,
  },
  settings: {
    navCollapsed: false,
    darkMode: localStorage.getItem('dark-mode-setting') || 'auto',
    theme: localStorage.getItem('admin-view') || 'base',
    logo: 'assets/images/logo.png',
    innerPageSideBarCollapsed: false,
    lang: localStorageLanguage || 'ar',
    LanguageId: Languages[localStorageLanguage],
    dir: localStorageLanguage === 'ar' ? Dir.RTL : Dir.LTR,
    innerSideBarCollapsed: false,
    country: undefined,
    countryV2: {
      data: 'jo',
      status: 'idle',
    },
  },
  isSliderMoving: false,
  searchParams: {},
  backNavigation: [],
  forwardNavigation: [],
  stripePaymentProduct: undefined,
};
const getLogo = (theme: string) => {
  switch (theme) {
    case 'base':
      return 'assets/images/logo.png';
    case 'super-admin':
      return 'assets/images/logo.png';

    default:
      break;
  }
  return '';
};
export const slice = createSlice({
  name: name,
  initialState,
  reducers: {
    clearAlert: (state) => {
      return {
        ...state,
        alert: initialState.alert,
      };
    },
    clearModalAlert: (state) => {
      return {
        ...state,
        alert: { ...initialState.alert, for: 'modal' },
      };
    },
    setAlert: (state, action: PayloadAction<Partial<AlertState>>) => {
      return {
        ...state,
        alert: {
          ...state.alert,
          ...action.payload,
        },
      };
    },
    resetError: (state) => {
      return {
        ...state,
        error: initialState.error,
      };
    },
    setError: (store, action: PayloadAction<ErrorState>) => {
      return {
        ...store,
        error: action.payload,
      };
    },
    resetLoading: (state) => {
      return {
        ...state,
        loading: initialState.loading,
      };
    },

    showLoading: (state, action: PayloadAction<LoadingState>) => {
      state.loading.show = action.payload.show;
      state.loading.ref = action.payload.ref;
    },
    showScreenLoading: (state, action: PayloadAction<LoadingState>) => {
      state.loading.show = action.payload.show;
      state.loading.ref = action.payload.ref;
      state.loading.isScreenLoader = true;
    },
    hideLoading: (store) => {
      return {
        ...store,
        loading: {
          ...initialState.loading,
          show: false,
          ref: null,
        },
      };
    },
    resetSettings: (state) => {
      return {
        ...state,
        settings: initialState.settings,
      };
    },
    setDir: (
      state,
      action: PayloadAction<typeof initialState.settings.dir>
    ) => {
      state.settings.dir = action.payload;
    },
    setNavBarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.settings.navCollapsed = action.payload;
    },
    setTheme: (state, action: PayloadAction<string | null>) => {
      const logoSrc = action.payload && getLogo(action.payload);

      state.settings.theme = action.payload;
      state.settings.logo = logoSrc;
    },
    setDarkMode: (state, action: PayloadAction<string | null>) => {
      state.settings.darkMode = action.payload;

      action.payload &&
        localStorage.setItem('dark-mode-setting', action.payload);
    },
    toggleCollapsedInnerSideBar: (state) => {
      state.settings.innerSideBarCollapsed =
        !state.settings.innerSideBarCollapsed;
    },
    toggleInnerPageLayoutSideBarCollapsed: (state) => {
      state.settings.innerPageSideBarCollapsed =
        !state.settings.innerPageSideBarCollapsed;
    },
    setLang: (
      state,
      action: PayloadAction<typeof initialState.settings.lang>
    ) => {
      state.settings = {
        ...state.settings,
        lang: action.payload,
        dir: localizationSystemValues[action.payload].dir,
        LanguageId: localizationSystemValues[action.payload].key,
      };
    },
    setCountry: (state, action: PayloadAction<string | null>) => {
      state.settings.country = action.payload;
    },
    setIsSliderMoving: (state, action) => {
      state.isSliderMoving = action.payload;
    },
    setSearchParams: (
      state,
      action: PayloadAction<{
        [key: string]: {
          pathname: string;
          search: { [key: string]: string | null | undefined };
        };
      }>
    ) => {
      state.searchParams = { ...state.searchParams, ...action.payload };
    },
    addBackNavigationItem: (state, action) => {
      const backNavigationLength = current(state).backNavigation?.length;

      let isSearchEmpty = false;

      const payloadSearch = action.payload.search;

      if (payloadSearch) {
        let searchEmptyLength = 0;

        const payloadSearchLength = Object.keys(payloadSearch).length;

        for (const p in payloadSearch) {
          if (!payloadSearch[p]) {
            searchEmptyLength++;
          }
        }

        if (searchEmptyLength === payloadSearchLength) {
          isSearchEmpty = true;
        }
      }

      const isObjectsEqual = (
        firstObj: { [key: string]: string | null | undefined } | undefined,
        secondObj: { [key: string]: string | null | undefined } | undefined
      ) => {
        if (firstObj && secondObj) {
          if (Object.keys(firstObj).length !== Object.keys(secondObj).length) {
            return false;
          } else {
            for (const s in firstObj) {
              if (!(s in secondObj) || secondObj[s] !== firstObj[s]) {
                return false;
              }
            }
          }
        } else if ((!firstObj || !secondObj) && firstObj !== secondObj) {
          return false;
        }

        return true;
      };

      if (backNavigationLength) {
        const lastItemSeach =
          current(state).backNavigation?.[backNavigationLength - 1].search;

        const isSearchEqual = isObjectsEqual(lastItemSeach, payloadSearch);

        if (
          (state.backNavigation?.[backNavigationLength - 1].pathname !==
            action.payload.pathname &&
            !isSearchEmpty) ||
          (!isSearchEqual && !isSearchEmpty)
        ) {
          state.backNavigation = state.backNavigation && [
            ...state.backNavigation,
            action.payload,
          ];

          const forwardNavigationLength =
            current(state).forwardNavigation?.length;
          const lastForwardNavigationItem =
            forwardNavigationLength &&
            current(state).forwardNavigation?.[forwardNavigationLength - 1];

          if (lastForwardNavigationItem) {
            if (!action.payload.forwardable) {
              state.forwardNavigation = [];
            }
          }
        }
      } else if (!isSearchEmpty || action.payload.forceAdd) {
        state.backNavigation = state.backNavigation && [
          ...state.backNavigation,
          action.payload,
        ];
      }
    },
    removeBackNavigationItem: (
      state,
      action: PayloadAction<number | undefined>
    ) => {
      const itemIndex =
        action.payload !== undefined
          ? action.payload
          : state.backNavigation?.length && state.backNavigation?.length - 1;

      state.backNavigation = state.backNavigation?.filter(
        (b, i) => i !== itemIndex
      );
    },
    addForwardNavigationItem: (state, action) => {
      if (state.forwardNavigation) {
        state.forwardNavigation = [...state.forwardNavigation, action.payload];
      }
    },
    removeForwardNavigationItem: (state) => {
      state.forwardNavigation?.pop();
    },
    setStripePaymentProduct: (state, action) => {
      state.stripePaymentProduct = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getUserCountryAsync.fulfilled, (state, action) => {
      if (action.payload) {
        const country = action.payload.split(';')[2];
        state.settings.country = country;
      }
    });
    // .addCase(getUserCountryV2Async.pending, (state) => {
    //   state.settings.countryV2.status = 'loading';
    // })
    // .addCase(getUserCountryV2Async.fulfilled, (state, action) => {
    //   state.settings.countryV2 = {
    //     data: action.payload,
    //     status: 'idle',
    //   };
    // })
    // .addCase(getUserCountryV2Async.rejected, (state) => {
    //   state.settings.countryV2.status = 'failed';
    // })
  },
});

export const actions = slice.actions;

export default slice.reducer;
